import { useState } from 'react'
import { useProjectStore } from '../../../hooks/use-project-store'
import { toast } from 'sonner'
import { useProjectPublish } from '@/hooks/use-project-publish'
import { useClearShareProjectCache } from '@/hooks/use-clear-swr-cache'
import { useTranslations } from 'next-intl'
import { mutate } from 'swr/_internal'

export type SharePlatform = 'twitter' | 'linkedin' | 'xiaohongshu'

interface ShareSendCoinsProps {
  onShareSuccess?: () => void
  onShareError?: (error: string) => void
}

interface ShareDescriptionResponse {
  content: string
}

interface ShareResponse {
  shareId: string
}

const PLATFORM_URL_PATTERNS: Record<SharePlatform, RegExp> = {
  twitter: /(twitter\.com|x\.com)/i,
  linkedin: /linkedin\.com/i,
  xiaohongshu: /xiaohongshu\.com/i,
}

const PLATFORM_NAMES: Record<SharePlatform, string> = {
  twitter: 'Twitter',
  linkedin: 'LinkedIn',
  xiaohongshu: 'RedNote',
}

export function useShareSendCoins({
  onShareSuccess,
  onShareError,
}: ShareSendCoinsProps = {}) {
  const t = useTranslations('share-send-coins')
  const [step, setStep] = useState(1)
  const [selectedPlatform, setSelectedPlatform] =
    useState<SharePlatform | null>(null)
  const [verificationUrl, setVerificationUrl] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shareId, setShareId] = useState<string | null>(null)
  const [shareContent, setShareContent] = useState('')
  const [isLoadingContent, setIsLoadingContent] = useState(false)
  const [log, setLog] = useState('')
  const { clear: clearShareProjectCache } = useClearShareProjectCache()

  const { project } = useProjectStore()
  const { publish, isPublishing } = useProjectPublish()
  const [publishUrl, setPublishUrl] = useState('')

  const handleSelectPlatform = async (platform: SharePlatform) => {
    try {
      setIsLoadingContent(true)
      setSelectedPlatform(platform)

      // First publish the project
      setLog(t('logs.publishing'))
      const url = await publish()
      if (url) {
        setPublishUrl(url.endsWith('index.html') ? url : `${url}index.html`)
      } else {
        setPublishUrl('')
      }
      setLog(t('logs.published'))

      // Get share description
      setLog(t('logs.getting_description'))

      if (platform !== 'xiaohongshu') {
        const descResponse = await fetch('/api/me/project/share/description', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            projectId: project!._id,
            platform,
          }),
        })

        if (!descResponse.ok) {
          throw new Error('Failed to get share description')
        }

        setLog(t('logs.description_fetched'))
        const descData: ShareDescriptionResponse = await descResponse.json()
        setShareContent(descData.content)
      }

      // Create share log
      setLog(t('logs.creating_share'))
      const shareResponse = await fetch('/api/me/project/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: project!._id,
          platform,
        }),
      })

      if (!shareResponse.ok) {
        throw new Error('Failed to create share')
      }

      const shareData: ShareResponse = await shareResponse.json()
      setLog(t('logs.share_created'))
      setShareId(shareData.shareId)

      setTimeout(() => {
        if (platform === 'xiaohongshu') {
          setStep(2)
        } else {
          document.getElementById(`${platform}-share-button`)?.click()
          setStep(2)
        }
      }, 20)
    } catch (err) {
      toast.error(t('share_failed'))
      throw err
    } finally {
      setIsLoadingContent(false)
      setLog('')
    }
  }

  const handleVerify = async () => {
    if (!shareId || !verificationUrl || !selectedPlatform) return

    console.log(
      'selectedPlatform',
      selectedPlatform,
      PLATFORM_URL_PATTERNS[selectedPlatform],
    )

    if (!PLATFORM_URL_PATTERNS[selectedPlatform].test(verificationUrl)) {
      const errorMsg = t('verification_error', {
        platform: PLATFORM_NAMES[selectedPlatform],
      })
      setError(errorMsg)
      onShareError?.(errorMsg)
      return
    }

    setIsVerifying(true)
    setError(null)

    try {
      const response = await fetch('/api/me/project/share', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shareId,
          shareUrl: verificationUrl,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed')
      }

      setError(null)
      setStep(3)
      onShareSuccess?.()
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to verify share'
      setError(errorMsg)
      setStep(3)
      onShareError?.(errorMsg)
    } finally {
      setIsVerifying(false)
      clearShareProjectCache()
      mutate('/api/me')
    }
  }

  const reset = () => {
    setStep(1)
    setSelectedPlatform(null)
    setVerificationUrl('')
    setError(null)
    setShareId(null)
    setShareContent('')
    setIsLoadingContent(false)
  }

  return {
    step,
    selectedPlatform,
    verificationUrl,
    isVerifying,
    error,
    shareContent,
    isLoadingContent,
    setStep,
    setVerificationUrl,
    handleSelectPlatform,
    handleVerify,
    reset,
    setError,
    isPublishing,
    log,
    publishUrl,
  }
}
